import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Goal, SelStandards, SEL_STANDARDS_AGE_GROUPS, AgeGroupKey } from 'src/app/models/goal/goal.model';
import { ActivatedRoute, Router } from '@angular/router';
import { GoalService } from 'src/app/services/goal.service';
import { SkillService } from 'src/app/services/skill.service';
import { UnitService } from 'src/app/services/unit.service';
import { DialogService } from 'src/app/services/dialog.service';
import { formatError } from 'src/app/utils/errorMessage.formatter';
import { HttpService } from 'src/app/services/http.service';
import { HeaderComponent } from 'src/app/shared/header/header.component';
import { LoaderComponent } from 'src/app/shared/loader/loader.component';
import { SelectModule } from 'src/app/shared/form/select/select.module';
import { ButtonModule } from 'src/app/shared/form/button/button.module';
import { TagfieldModule } from 'src/app/shared/form/tagfield/tagfield.module';
import { TextfieldComponent } from 'src/app/shared/form/textfield/textfield.component';
import { FilePickerComponent } from 'src/app/shared/form/file-picker/file-picker.component';
import { NamedFileListComponent } from 'src/app/shared/form/named-file-list/named-file-list.component';
import { RichtextareaComponent } from 'src/app/shared/form/richtextarea/richtextarea.component';
import { AssociatedUnitsListComponent } from 'src/app/shared/associated-units-list/associated-units-list.component';
import { SampleIepGoalsComponent } from '../sample-iep-goals/sample-iep-goals.component';

@Component({
  selector: 'app-goals-editor',
  templateUrl: './goals-editor.component.html',
  styleUrls: ['./goals-editor.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatTooltipModule,
    HeaderComponent,
    LoaderComponent,
    SelectModule,
    ButtonModule,
    TagfieldModule,
    TextfieldComponent,
    FilePickerComponent,
    NamedFileListComponent,
    RichtextareaComponent,
    AssociatedUnitsListComponent,
    SampleIepGoalsComponent,
  ],
})
export class GoalsEditorComponent {
  goal: Goal = {
    order: 0,
    title: '',
    goalId: '',
    skillId: '',
    downloads: [],
  };
  saving = false;
  loading = false;
  deleting = false;
  emptyMandatoryFields = Array<string>();
  availableOrders: Array<{ id: string; label: string }> = [];
  availableSkills: Array<{ id: string; label: string }> = [];
  unitsWithThisGoalId: Array<{
    age: string;
    ageUuid: string;
    ageOrder: number;
    hasChanges: boolean;
    units: Array<{ uuid: string; title: string; order: number; status: string }>;
  }> = [];
  hasUnsavedUnitChanges = false;

  // SEL Standards age groups configuration (imported from model)
  selStandardsAgeGroups = SEL_STANDARDS_AGE_GROUPS;

  // SEL Standards display properties (for textarea binding)
  selStandardsDisplay: Record<AgeGroupKey, Record<keyof SelStandards[AgeGroupKey], string>> = this.initializeSelStandardsDisplay();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private goalService: GoalService,
    private httpService: HttpService,
    private skillService: SkillService,
    private dialogService: DialogService,
    private unitService: UnitService
  ) {
    this.route.params.subscribe(data => {
      this.loading = true;
      this.loadAvailableSkills(data.goalId);
    });
  }

  loadAvailableSkills(goalId: string) {
    this.skillService.list({ limit: 1000 }).subscribe(response => {
      this.availableSkills = response.skills.map(s => {
        return { id: s.skillId, label: s.title };
      });

      if (goalId === 'new' && this.availableSkills.length > 0) {
        this.goal.skillId = this.availableSkills[0].id;
        // Initialize selStandards for new goals
        this.convertSelStandardsToDisplay();
        this.loadAvailableOrders(this.availableSkills[0].id, goalId);
      } else {
        this.load(goalId);
      }
    });
  }

  loadAvailableOrders(parentSkillId: string, goalId?: string) {
    this.availableOrders = [];
    this.goalService.list({ skill: parentSkillId, curriculumId: 3 }).subscribe(response => {
      for (var i = 0; i < response.totalResults; i++) {
        var order = (i + 1).toString();
        this.availableOrders.push({
          id: order,
          label: order,
        });
      }

      if (!this.goal.order || !goalId || goalId === 'new' || response.totalResults <= 0) {
        var newOrder = (this.availableOrders.length + 1).toString();
        this.availableOrders.push({
          id: newOrder,
          label: newOrder,
        });
        this.goal.order = this.availableOrders.length;
      }

      this.loading = false;
    });
  }

  loadUnitsWithThisGoalId(goalId: string) {
    this.goalService.get(goalId).subscribe(
      response => {
        if (response.goal.associatedUnitsByAge) {
          // Map the API response to our component structure
          this.unitsWithThisGoalId = response.goal.associatedUnitsByAge
            .map(ageGroup => ({
              ageTitle: ageGroup.ageTitle,
              ageUuid: ageGroup.ageUuid,
              ageOrder: ageGroup.ageOrder,
              hasChanges: false,
              units: ageGroup.units
                .map(unit => ({
                  uuid: unit.uuid,
                  title: unit.title || '',
                  order: unit.order || 0,
                  status: unit.status || 'draft',
                }))
                .sort((a, b) => a.order - b.order),
            }))
            .sort((a, b) => a.ageOrder - b.ageOrder);
          this.hasUnsavedUnitChanges = false;
        }
      },
      error => {
        this.dialogService.showErrorAlert('Error loading goal with units: ' + (error.message || 'Unknown error'));
      }
    );
  }

  load(goalId: string) {
    this.goalService.get(goalId).subscribe(
      response => {
        this.loading = false;
        this.goal = response.goal;
        if (!this.goal.tags) {
          this.goal.tags = [];
        }
        if (!this.goal.selStandards) {
          this.goal.selStandards = this.initializeSelStandards();
        } else if (!this.goal.selStandards.sampleIepGoals || this.goal.selStandards.sampleIepGoals.length === 0) {
          this.goal.selStandards.sampleIepGoals = [{ title: '', description: '' }];
        }

        // Convert selStandards to display format
        this.convertSelStandardsToDisplay();

        this.loadAvailableOrders(this.goal.skillId, this.goal.goalId);
        this.loadUnitsWithThisGoalId(goalId);
      },
      errors => {
        this.loading = false;
        this.dialogService.showErrorAlert(formatError(errors?.error));
        this.goBack();
      }
    );
  }

  trySave() {
    if (!this.allFieldsAreValid()) {
      return;
    }

    // Convert selStandards from display format back to API format
    this.convertSelStandardsToApiFormat();

    this.saving = true;

    // First, save any unit order changes
    this.saveUnitOrderChanges()
      .then(() => {
        // Then upload media files
        var mediaPromises = this.getMediaReadyForUpload();
        return Promise.all(mediaPromises);
      })
      .then(() => {
        // Finally, save the goal
        this.goalService.save(this.goal).subscribe(
          response => {
            this.saving = false;
            this.dialogService.showSuccessAlert('Save Successful.');
            if (response) {
              this.router.navigate(['/goals/' + response.goalId]);
            }
          },
          errors => {
            this.saving = false;
            this.dialogService.showErrorAlert(formatError(errors?.error));
          }
        );
      })
      .catch(error => {
        this.saving = false;
        this.dialogService.showErrorAlert('Error saving unit changes: ' + (error.message || 'Unknown error'));
      });
  }

  allFieldsAreValid(): boolean {
    this.emptyMandatoryFields = [];
    if (!this.goal.title) {
      this.emptyMandatoryFields.push('title');
    }
    if (this.goal.videoFile && (!this.goal.videoThumbnailFile || this.goal.videoThumbnail)) {
      this.emptyMandatoryFields.push('videoThumbnail');
    }
    if (this.goal.downloads) {
      this.goal.downloads.forEach((d, i) => {
        if (d.file && !d.title) {
          this.emptyMandatoryFields.push('Visual Support' + i);
        }
      });
    }

    if (this.emptyMandatoryFields.length > 0) {
      setTimeout(() => {
        var errorMessage = document.querySelector('.error-message');
        errorMessage?.scrollIntoView({ behavior: 'smooth' });
      }, 1000);
    }

    return this.emptyMandatoryFields.length <= 0;
  }

  getMediaReadyForUpload(): Promise<string>[] {
    const promises: Promise<string>[] = [];

    if (this.goal.videoFile) {
      promises.push(
        this.createPromiseForMedia(this.goal.videoFile, 'video').then(url => {
          this.goal.videoLink = url;
          this.goal.videoFile = null;
          return url;
        })
      );
    }

    if (this.goal.videoThumbnailFile) {
      promises.push(
        this.createPromiseForMedia(this.goal.videoThumbnailFile, 'image').then(url => {
          this.goal.videoThumbnail = url;
          this.goal.videoThumbnailFile = null;
          return url;
        })
      );
    }

    if (this.goal.iconFile) {
      promises.push(
        this.createPromiseForMedia(this.goal.iconFile, 'image').then(url => {
          this.goal.icon = url;
          this.goal.iconFile = null;
          return url;
        })
      );
    }

    if (this.goal.downloads && this.goal.downloads.length > 0) {
      this.goal.downloads.forEach(d => {
        if (d.file) {
          promises.push(
            this.createPromiseForMedia(d.file, 'image').then(url => {
              d.url = url;
              d.file = null;
              d.thumbnail = url.replace(/\.[^\.]+$/, '.png');
              return url;
            })
          );
        }
      });
    }

    return promises;
  }

  createPromiseForMedia(file: File, type: 'video' | 'image'): Promise<string> {
    return new Promise((resolve, reject) => {
      this.httpService.uploadMedia(file, type).subscribe(
        r => resolve(r.secure_url),
        error => reject(error)
      );
    });
  }

  delete() {
    this.deleting = true;
    this.goalService.delete(this.goal.goalId).subscribe(() => {
      this.deleting = false;
      this.dialogService.showSuccessAlert('Delete Successful.');
      this.router.navigate(['/goals']);
    });
  }

  confirmDelete() {
    this.dialogService.showConfirmationDialog({
      type: 'danger',
      confirmButtonLabel: 'Delete',
      onConfirmCallback: () => this.delete(),
      dialogIconUrl: '../../../../assets/images/icons/alert-circle.svg',
      headerMessage: 'Warning: Deleting a goal will REMOVE ' + 'related units from the Full Curriculum page',
      confirmButtonIconUrl: '../../../../assets/images/icons/delete.svg',
      title: 'Are you sure you want to remove ' + this.goal.title + ' goal from the Curriculum?',
      message: 'This action cannot be undone.',
    });
  }

  onSkillChange($event: string) {
    this.goal.order = 0;
    this.loadAvailableOrders($event, this.goal.goalId);
  }

  goBack() {
    this.router.navigate(['/goals']);
  }

  onUnitsChanged(updatedUnits: any[]) {
    this.unitsWithThisGoalId = updatedUnits;
  }

  saveUnitOrderChanges(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Find all age groups with changes
      const ageGroupsWithChanges = this.unitsWithThisGoalId.filter(ag => ag.hasChanges);

      if (ageGroupsWithChanges.length === 0) {
        resolve();
        return;
      }

      // Collect all units that need to be updated
      const unitUpdatePromises: Promise<any>[] = [];

      ageGroupsWithChanges.forEach(ageGroup => {
        ageGroup.units.forEach((unit, index) => {
          const newOrder = index + 1; // 1-based index as the new order

          // Create a promise for each unit update
          const updatePromise = new Promise((resolveUnit, rejectUnit) => {
            this.unitService.get(unit.uuid).subscribe(
              fullUnit => {
                // Update the order on the full unit object
                fullUnit.unit.order = newOrder;

                // Save the updated unit
                this.unitService.save(fullUnit.unit).subscribe(
                  response => {
                    resolveUnit(response);
                  },
                  error => {
                    rejectUnit(new Error(`Failed to save unit ${unit.title}: ${error.message || 'Unknown error'}`));
                  }
                );
              },
              error => {
                rejectUnit(new Error(`Failed to fetch unit ${unit.title}: ${error.message || 'Unknown error'}`));
              }
            );
          });

          unitUpdatePromises.push(updatePromise);
        });
      });

      // Wait for all unit updates to complete
      Promise.all(unitUpdatePromises)
        .then(() => {
          // Reset the hasChanges flags since changes are now saved
          this.unitsWithThisGoalId.forEach(ag => (ag.hasChanges = false));
          this.hasUnsavedUnitChanges = false;
          resolve();
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  // Initialize empty selStandards display structure
  initializeSelStandardsDisplay(): Record<AgeGroupKey, Record<keyof SelStandards[AgeGroupKey], string>> {
    const selStandardsDisplay: any = {};
    SEL_STANDARDS_AGE_GROUPS.forEach(({ key }) => {
      selStandardsDisplay[key] = {
        selGoals: '',
        learningStandards: '',
        standardizedBenchmarks: '',
      };
    });
    return selStandardsDisplay;
  }

  // Initialize empty selStandards structure
  initializeSelStandards(): SelStandards {
    const emptyCategory = () => ({ selGoals: [], learningStandards: [], standardizedBenchmarks: [] });
    return {
      'pre-k-kindergarten': emptyCategory(),
      'elementary-school': emptyCategory(),
      'middle-high-school': emptyCategory(),
      sampleIepGoals: [{ title: '', description: '' }],
    };
  }

  // Convert selStandards from API format (arrays) to display format (newline-separated strings)
  convertSelStandardsToDisplay() {
    if (!this.goal.selStandards) {
      this.goal.selStandards = this.initializeSelStandards();
    }
    if (!this.goal.selStandards.sampleIepGoals || this.goal.selStandards.sampleIepGoals.length === 0) {
      this.goal.selStandards.sampleIepGoals = [{ title: '', description: '' }];
    }

    SEL_STANDARDS_AGE_GROUPS.forEach(({ key }) => {
      this.selStandardsDisplay[key].selGoals = (this.goal.selStandards![key].selGoals || []).join('\n');
      this.selStandardsDisplay[key].learningStandards = (this.goal.selStandards![key].learningStandards || []).join('\n');
      this.selStandardsDisplay[key].standardizedBenchmarks = (this.goal.selStandards![key].standardizedBenchmarks || []).join('\n');
    });
  }

  // Convert selStandards from display format (newline-separated strings) to API format (arrays)
  // Transforms textarea strings like "Goal 1\nGoal 2\n\nGoal 3" into arrays like ['Goal 1', 'Goal 2', 'Goal 3']
  // Removes empty lines and trims whitespace from each item before sending to API
  convertSelStandardsToApiFormat() {
    if (!this.goal.selStandards) {
      this.goal.selStandards = this.initializeSelStandards();
    }

    SEL_STANDARDS_AGE_GROUPS.forEach(({ key }) => {
      this.goal.selStandards![key].selGoals = this.selStandardsDisplay[key].selGoals
        .split('\n')
        .map(item => item.trim())
        .filter(item => item.length > 0);

      this.goal.selStandards![key].learningStandards = this.selStandardsDisplay[key].learningStandards
        .split('\n')
        .map(item => item.trim())
        .filter(item => item.length > 0);

      this.goal.selStandards![key].standardizedBenchmarks = this.selStandardsDisplay[key].standardizedBenchmarks
        .split('\n')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    });
  }
}
